import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as sched from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useSchedule } from '../../hooks/useSchedule';
import { FuzzyGanttChart, FuzzyScheduleResult } from '../charts/FuzzyGanttChart';

interface PersonalForecastProps {
  personId: string;
  dependencyGraph: sched.WrappedDependencyGraph | null;
}

/**
 * Personal forecast view: filters the workspace fuzzy schedule to items
 * this person works on, plus outcomes that depend on those tasks.
 */
export const PersonalForecast: React.FC<PersonalForecastProps> = ({ personId, dependencyGraph }) => {
  const { database } = useDatabase();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { schedule: fuzzySchedule, loading: scheduleLoading } = useSchedule();

  const { scheduleResult, nodeToEntity } = useMemo(() => {
    if (!fuzzySchedule || !database) {
      return { scheduleResult: null, nodeToEntity: {} };
    }
    const adapted = adaptForPerson(fuzzySchedule as any, personId, dependencyGraph, database);
    const n2e = (fuzzySchedule as any)?.meta?.node_to_entity || {};
    return { scheduleResult: adapted, nodeToEntity: n2e };
  }, [fuzzySchedule, personId, database, dependencyGraph]);

  if (scheduleLoading) {
    return (
      <div className="flex items-center gap-3 justify-center p-8 text-cm-text-muted text-sm">
        <div className="w-5 h-5 border-2 border-cm-accent-orange border-t-transparent rounded-full animate-spin" />
        Building forecast...
      </div>
    );
  }

  if (!scheduleResult) {
    return (
      <div className="p-6 text-center text-cm-text-muted text-sm">
        No forecast data available. Set priorities to enable forecasting.
      </div>
    );
  }

  const unschedulable = scheduleResult.schedule?.unschedulable ?? {};
  const hasItems = Object.keys(scheduleResult.schedule?.items ?? {}).length > 0;
  const hasUnschedulable = Object.keys(unschedulable).length > 0;

  return (
    <div className="space-y-4">
      {hasItems && (
        <FuzzyGanttChart
          result={scheduleResult}
          startDate={new Date()}
          nodeToEntity={nodeToEntity}
          linkBase={workspaceId ? `/workspaces/${workspaceId}` : undefined}
        />
      )}
      {hasUnschedulable && (
        <div className="rounded-xl border border-gray-800 bg-gray-950 p-5">
          <h4 className="text-sm font-semibold text-amber-400/80 mb-3">
            Blocked Items
          </h4>
          <ul className="space-y-2 text-sm">
            {Object.entries(unschedulable).map(([nodeId, item]: [string, any]) => (
              <li key={nodeId} className="flex items-start gap-2 text-gray-400">
                <span className="text-amber-500/60 mt-0.5">&#x26a0;</span>
                <div>
                  <span className="text-gray-200">{item.label}</span>
                  <span className="text-gray-500 ml-2">— {item.reason}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!hasItems && !hasUnschedulable && (
        <div className="p-6 text-center text-cm-text-muted text-sm">
          No forecast data available. Set priorities to enable forecasting.
        </div>
      )}
    </div>
  );
};

/**
 * Adapt a DatabaseFuzzyScheduleResult for a single person.
 * Includes tasks this person works on, plus outcomes that depend on those tasks.
 */
function adaptForPerson(
  dbResult: any,
  personId: string,
  dependencyGraph: sched.WrappedDependencyGraph | null,
  database: sched.WrappedDatabase | null,
): FuzzyScheduleResult {
  const meta = dbResult.meta;
  const scheduleData = dbResult.schedule;
  const stats = dbResult.stats || {};
  const items = scheduleData.items as Record<string, any>;
  const nodeToEntity = meta?.node_to_entity ?? {};

  // Find resource IDs for this person
  const personResourceIds = new Set<string>();
  const resourceToPerson: Record<string, string> = {};
  for (const [resourceId, pid] of Object.entries(meta.resource_to_person as Record<string, string>)) {
    resourceToPerson[resourceId] = (meta.person_labels as Record<string, string>)[pid as string] || (pid as string);
    if (pid === personId) {
      personResourceIds.add(resourceId);
    }
  }

  // Build set of entity IDs this person has manual allocations for
  const allocatedEntityIds = new Set<string>();
  if (database) {
    const personAllocations: any[] = database.manual_allocations_for_person?.(personId) ?? [];
    for (const pa of personAllocations) {
      allocatedEntityIds.add(pa.entity);
    }
  }

  // First pass: find items where this person contributes OR has allocations
  // (tasks fully covered by manual allocations become milestones with no
  // resource_contributions, but should still appear in the forecast)
  const personNodeIds = new Set<string>();
  const personEntityIds = new Set<string>();
  for (const [nodeId, item] of Object.entries(items)) {
    const contributions = (item as any).resource_contributions ?? {};
    const hasContributions = Object.keys(contributions).some(rid => personResourceIds.has(rid));
    const entity = nodeToEntity[nodeId];
    const hasAllocation = entity?.id && allocatedEntityIds.has(entity.id);

    if (hasContributions || hasAllocation) {
      personNodeIds.add(nodeId);
      if (entity?.id) personEntityIds.add(entity.id);
    }
  }

  // Second pass: also include outcomes that depend on the person's tasks
  if (dependencyGraph) {
    for (const [nodeId, item] of Object.entries(items)) {
      if (personNodeIds.has(nodeId)) continue;
      const entity = nodeToEntity[nodeId];
      if (!entity || entity.type !== 'Outcome') continue;

      // Check if any of this outcome's deep dependencies are tasks this person works on
      try {
        const deps = dependencyGraph.get_deep_dependencies(
          { type: 'Outcome', id: entity.id } as unknown as sched.DependencyNodeId,
        );
        for (const dep of deps) {
          const depId = (dep as any).id;
          if (depId && personEntityIds.has(depId)) {
            personNodeIds.add(nodeId);
            break;
          }
        }
      } catch {
        // Node may not be in dependency graph
      }
    }
  }

  // Build filtered items
  const filteredItems: Record<string, any> = {};
  for (const nodeId of personNodeIds) {
    filteredItems[nodeId] = items[nodeId];
  }

  // Build pool structure: single pool for this person
  const personLabel = (meta.person_labels as Record<string, string>)?.[personId] ?? personId;
  const poolResources: Record<string, string[]> = {};
  const resourceToPool: Record<string, string> = {};
  for (const rid of personResourceIds) {
    poolResources[personLabel] = poolResources[personLabel] ?? [];
    poolResources[personLabel].push(rid);
    resourceToPool[rid] = personLabel;
  }

  // Ensure items with no resource_contributions (milestones, fully-allocated tasks)
  // are assigned to the correct pool based on their entity type.
  // Tasks go into the person's pool; outcomes stay ungrouped.
  const patchedItems = { ...filteredItems };
  const syntheticResourceId = `__person_${personId}`;
  let needsSynthetic = false;

  for (const [nodeId, item] of Object.entries(filteredItems)) {
    const contributions = (item as any).resource_contributions ?? {};
    if (Object.keys(contributions).length > 0) continue;

    const entity = nodeToEntity[nodeId];
    if (entity?.type === 'Outcome') continue; // Outcomes stay ungrouped

    // Task/milestone with no contributions — inject a synthetic contribution
    // so FuzzyGanttChart places it in the person's pool
    needsSynthetic = true;
    patchedItems[nodeId] = {
      ...item,
      resource_contributions: { [syntheticResourceId]: 0 },
    };
  }

  if (needsSynthetic) {
    if (!poolResources[personLabel]) poolResources[personLabel] = [];
    poolResources[personLabel].push(syntheticResourceId);
    resourceToPool[syntheticResourceId] = personLabel;
  }

  return {
    schedule: {
      items: patchedItems,
      unschedulable: scheduleData.unschedulable || {},
      resolution: scheduleData.resolution,
    },
    meta: {
      node_to_step: {},
      node_labels: meta.node_labels || {},
      resource_to_pool: resourceToPool,
      pool_resources: poolResources,
      conditional_nodes: [],
      warnings: meta.warnings || [],
    },
    stats,
  } as unknown as FuzzyScheduleResult;
}
